/**
 ShapeDiver 3D Viewer Application

 MaterialPresets.js

 Content:
   Library of material presets and handler function

  Copyright (c) 2018 ShapeDiver GmbH

  @module MaterialPresets
 */


/**
 * JSON preset material typedef
 * @typedef {Object} JSONPresetMaterial
 * @version 1.0
 * @see module:JSONMaterial~JSONColor
 * @property {String} version - "1.0"
 * @property {module:JSONMaterial~JSONColor} color - Material color
 * @property {Integer} preset - Preset description, see {@link XXX} for description of the different presets
 */

/**
 * Import three
 * @ignore
 */
var THREE = require('../../externals/three');

/**
 * @classdesc Material preset container singleton
 * @class
 * @hideconstructor
 */
var MaterialPresets = function () {

  var that = this;

  /**
   * Location of material textures
   * @type {String}
   * @private
   * @ignore
   */
  var texturePath = 'https://viewer.shapediver.com/textures_presets';

  /**
   * Define read-only material member
   * @private
   * @param  {module:JSONMaterial~JSONMaterial(2)} mat  The material object
   */
  var injectMaterial = function (mat) {
    var obj = {};
    Object.keys(mat).forEach(function (key) {
      Object.defineProperty(obj, key, {
        enumerable: true,
        readable: true,
        configurable: false,
        get: function () {
          return mat[key];
        },
        set: function () { }, // Comment regarding expo AR template: expo seems to complain about no setter, adding an empty one
      });
    });
    Object.defineProperty(obj, 'version', {
      enumerable: true,
      readable: true,
      configurable: false,
      get: function () {
        return '2.0';
      },
      set: function () { }, // Comment regarding expo AR template: expo seems to complain about no setter, adding an empty one
    });
    return obj;
  };

  /**
   * Default material, used if no material is defined
   *
   * Id: 0100
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 3.0
   */
  this.defaultMaterial = injectMaterial({
    color: [211, 211, 211, 255],
    bumpAmplitude: 1.0,
    roughness: 1.0,
    metalness: 0.0
  });


  /**
   * Groundplane material, used only for scene groundplane object
   *
   * Id: 0101
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.groundplaneMaterial = injectMaterial({
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    side: THREE.FrontSide
  });

  /**
   * Default plastic material
   *
   * Id: 0200
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.defaultPlasticMaterial = injectMaterial({
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    normaltexture: texturePath + '/02/00/normalMap.jpg',
    roughnesstexture: texturePath + '/02/00/roughnessMap.jpg'
  });

  /**
   * Default metal material
   *
   * Id: 0300
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.defaultMetalMaterial = injectMaterial({
    color: [205, 205, 205, 255],
    roughness: 0.25,
    metalness: 1.0
  });

  /**
   * Used metal material
   *
   * Id: 0301
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.usedMetalMaterial = injectMaterial({
    color: [205, 205, 205, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: texturePath + '/03/01/roughnessMap.jpg'
  });

  /**
   * Metal material varation 1
   *
   * Id: 0302
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.metalVariation1Material = injectMaterial({
    color: [205, 205, 205, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: texturePath + '/03/02/roughnessMap.jpg'
  });

  /**
   * Metal material varation 2
   *
   * Id: 0303
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.metalVariation2Material = injectMaterial({
    color: [205, 205, 205, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: texturePath + '/03/03/roughnessMap.jpg'
  });

  /**
   * Gold material
   *
   * Id: 0310
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.goldMaterial = injectMaterial({
    color: [230, 207, 92, 255],
    roughness: 0.0,
    metalness: 1.0
  });

  /**
   * Used gold material
   *
   * Id: 0311
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.usedGoldMaterial = injectMaterial({
    color: [230, 207, 92, 255],
    roughness: 1.0,
    metalness: 1.0,
    roughnesstexture: texturePath + '/03/11/roughnessMap.jpg'
  });

  /**
   * Default glass material
   *
   * Id: 0400
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.defaultGlassMaterial = injectMaterial({
    color: [211, 211, 211, 255],
    roughness: 0.0,
    metalness: 1.0,
    transparency: 0.75
  });

  /**
   * Default wood material
   *
   * Id: 0500
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.defaultWoodMaterial = injectMaterial({
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: texturePath + '/05/00/map.jpg',
    normaltexture: texturePath + '/05/00/normalMap.jpg',
    metalnesstexture: texturePath + '/05/00/metalnessMap.jpg',
    roughnesstexture: texturePath + '/05/00/roughnessMap.jpg'
  });

  /**
   * Wood floor material
   *
   * Id: 0501
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.woodFloorMaterial = injectMaterial({
    color: [211, 211, 211, 255],
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: texturePath + '/05/01/map.jpg',
    normaltexture: texturePath + '/05/01/normalMap.jpg',
    metalnesstexture: texturePath + '/05/01/metalnessMap.jpg',
    roughnesstexture: texturePath + '/05/01/roughnessMap.jpg'
  });

  /**
   * Natural oak material
   *
   * Id: 0510
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.naturalOakMaterial = injectMaterial({
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: texturePath + '/05/10/map.jpg',
    normaltexture: texturePath + '/05/10/normalMap.jpg',
    roughnesstexture: texturePath + '/05/10/roughnessMap.jpg'
  });

  /**
   * Natural oak material
   *
   * Id: 0511
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.premiumOakMaterial = injectMaterial({
    roughness: 1.0,
    metalness: 0.0,
    bitmaptexture: texturePath + '/05/11/map.jpg',
    roughnesstexture: texturePath + '/05/11/roughnessMap.jpg'
  });


  /**
   * Shortcut function to create leather material presets
   * @private
   * @ignore
   * @param  {String} materialSpecificId Id of material within the leather group
   */
  var injectLeather = function (materialSpecificId) {
    return injectMaterial({
      roughness: 1.0,
      metalness: 0.0,
      bitmaptexture: texturePath + '/06/' + materialSpecificId + '/map.jpg',
      normaltexture: texturePath + '/06/' + materialSpecificId + '/normalMap.jpg',
      metalnesstexture: texturePath + '/06/' + materialSpecificId + '/metalnessMap.jpg',
      roughnesstexture: texturePath + '/06/' + materialSpecificId + '/roughnessMap.jpg'
    });
  };


  /**
   * Default leather material
   *
   * Id: 0600
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.defaultLeatherMaterial = injectLeather('00');

  /**
   * Dark brown leather material
   *
   * Id: 0601
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.darkBrownLeatherMaterial = injectLeather('01');

  /**
   * Black leather material
   *
   * Id: 0602
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.blackLeatherMaterial = injectLeather('02');


  /**
   * Worn leather material
   *
   * Id: 0610
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.wornLeatherMaterial = injectLeather('10');

  /**
   * Default fabric material
   *
   * Id: 0700
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.defaultFabricMaterial = injectMaterial({
    roughness: 1.0,
    metalness: 1.0,
    bitmaptexture: texturePath + '/07/00/map.jpg',
    normaltexture: texturePath + '/07/00/normalMap.jpg',
    roughnesstexture: texturePath + '/07/00/roughnessMap.jpg',
    metalnesstexture: texturePath + '/07/00/metalnessMap.jpg'
  });

  /**
   * Grey fabric material
   *
   * Id: 0701
   *
   * @type {module:JSONMaterial~JSONMaterial(2)}
   * @public
   * @version 2.0
   */
  this.greyFabricMaterial = injectMaterial({
    roughness: 1.0,
    metalness: 1.0,
    bitmaptexture: texturePath + '/07/01/map.jpg',
    normaltexture: texturePath + '/07/01/normalMap.jpg',
    roughnesstexture: texturePath + '/07/01/roughnessMap.jpg',
    metalnesstexture: texturePath + '/07/01/metalnessMap.jpg'
  });


  /**
   * Return preset material by id
   * @public
   * @function
   * @param  {Integer} id The id of the preset. See table below.
   * @return {module:JSONMaterial~JSONMaterial}    JSON material of version 2.0
   */
  this.fromId = Object.defineProperty(that, 'fromId', {
    readable: true,
    configurable: false,
    enumerable: true,
    get: function () {
      return function (id) {

        id = Number(String(id));


        // for a while, we had documented the presets to be 10, 20, 30 and 40
        // here, we allow for the few cases where this was used to succeed
        if (id < 100 && id % 10 == 0) {
          id /= 10;
        }

        if (Math.floor(Math.log(id) / Math.log(10)) == 0) id *= 100;
        var materialClassID = Math.floor(id / 100);
        var materialSpecificID = id - (materialClassID * 100);

        switch (materialClassID) {
          //default
          case 1:
            switch (materialSpecificID) {
              //groundplane
              case 1:
                return this.groundplaneMaterial;
              //default default
              default:
                return this.defaultMaterial;
            }
          //plastic
          case 2:
            switch (materialSpecificID) {
              //plastic default
              default: return this.defaultPlasticMaterial;
            }
          //metal
          case 3:
            switch (materialSpecificID) {
              //metal used
              case 1:
                return this.usedMetalMaterial;
              //metal variation 1
              case 2:
                return this.metalVariation1Material;
              //metal variation 2
              case 3:
                return this.metalVariation2Material;
              //gold
              case 10:
                return this.goldMaterial;
              //gold used
              case 11:
                return this.usedGoldMaterial;
              //metal default
              default:
                return this.defaultMetalMaterial;
            }
          //glass
          case 4:
            switch (materialSpecificID) {
              //glass default
              default: return this.defaultGlassMaterial;
            }
          //wood
          case 5:
            switch (materialSpecificID) {
              //wood floor
              case 1:
                return this.woodFloorMaterial;
              //natural oak
              case 10:
                return this.naturalOakMaterial;
              //premium oak
              case 11:
                return this.premiumOakMaterial;
              //wood default
              default:
                return this.defaultWoodMaterial;
            }
          //leather
          case 6:
            switch (materialSpecificID) {
              //leather dark brown
              case 1:
                return this.darkBrownLeatherMaterial;
              //leather black
              case 2:
                return this.blackLeatherMaterial;
              //leather worn
              case 10:
                return this.wornLeatherMaterial;
              //leather default
              default:
                return this.defaultLeatherMaterial;
            }
          //fabric
          case 7:
            switch (materialSpecificID) {
              //fabric grey
              case 1:
                return this.greyFabricMaterial;
              //fabric default
              default:
                return this.defaultFabricMaterial;
            }
          //invalid class
          default:
            return this.defaultMaterial;
        }
      };
    },
    set: function () { }, // Comment regarding expo AR template: expo seems to complain about no setter, adding an empty one
  });

  return that;
};

module.exports = new MaterialPresets();
